/* ---------------------------------------------------------------------------
   מחשבון ההחזר (ROI) בדף המכירה — פונקציה טהורה, בלי React ובלי טקסט.

   המודל פשוט בכוונה, וכל ההנחות גלויות ונערכות בדף:
   - זמן אדמיניסטרציה: פניות בחודש × דקות לפנייה × אחוז החיסכון המונח.
   - כסף שנחסך: השעות שנחסכו × עלות שעת עבודה משרדית.
   - הכנסה נוספת: הצעות בחודש × שיפור אחוז הסגירה (בנקודות אחוז, עד 100%)
     × שווי עבודה ממוצע. זו הכנסה, לא רווח — הדף אומר את זה.
   - ההשפעה השנתית: (חיסכון + הכנסה נוספת) × 12.
   זו הערכה ולא הבטחה; אין כאן נתונים של לקוחות.
   --------------------------------------------------------------------------- */

import {
  monthlyPriceMinor,
  type BillingInterval,
  type PlanCatalog,
  type PlanKey,
} from '@/lib/plan-catalog';

export interface RoiInputs {
  /** פניות (בקשות שירות) בחודש. */
  requestsPerMonth: number;
  /** דקות של עבודה משרדית לכל פנייה היום (רישום, תיאום, עדכונים). */
  adminMinutesPerRequest: number;
  /** עלות שעת עבודה משרדית, ב-₪. */
  hourlyCost: number;
  /** שווי עבודה ממוצע, ב-₪. */
  avgJobValue: number;
  /** הצעות מחיר שנשלחות בחודש. */
  quotesPerMonth: number;
  /** אחוז הסגירה של הצעות היום (0–100). */
  closeRatePct: number;
}

export interface RoiAssumptions {
  /** כמה אחוז מזמן האדמיניסטרציה לפנייה נחסך (0–100). */
  adminReductionPct: number;
  /** בכמה נקודות אחוז עולה אחוז הסגירה. */
  closeRateLiftPts: number;
}

export interface RoiResult {
  hoursSavedPerMonth: number;
  moneySavedPerMonth: number;
  /** עבודות נוספות בחודש (יכול להיות שבר — ממוצע). */
  extraJobsPerMonth: number;
  extraRevenuePerMonth: number;
  monthlyImpact: number;
  yearlyImpact: number;
}

export const DEFAULT_INPUTS: RoiInputs = {
  requestsPerMonth: 120,
  adminMinutesPerRequest: 10,
  hourlyCost: 80,
  avgJobValue: 1500,
  quotesPerMonth: 40,
  closeRatePct: 35,
};

export const DEFAULT_ASSUMPTIONS: RoiAssumptions = {
  adminReductionPct: 60,
  closeRateLiftPts: 5,
};

/** מספר לא תקין או שלילי → 0; אחוזים עד 100. */
const nonNeg = (n: number) => (Number.isFinite(n) && n > 0 ? n : 0);
const pct = (n: number) => Math.min(100, nonNeg(n));

export function calculateRoi(inputs: RoiInputs, assumptions: RoiAssumptions): RoiResult {
  const requests = nonNeg(inputs.requestsPerMonth);
  const minutes = nonNeg(inputs.adminMinutesPerRequest);
  const hourly = nonNeg(inputs.hourlyCost);
  const jobValue = nonNeg(inputs.avgJobValue);
  const quotes = nonNeg(inputs.quotesPerMonth);
  const closeRate = pct(inputs.closeRatePct);
  const reduction = pct(assumptions.adminReductionPct);
  // אחוז הסגירה לא יכול לעבור 100%.
  const lift = Math.min(nonNeg(assumptions.closeRateLiftPts), 100 - closeRate);

  const hoursSavedPerMonth = (requests * minutes * (reduction / 100)) / 60;
  const moneySavedPerMonth = hoursSavedPerMonth * hourly;
  const extraJobsPerMonth = quotes * (lift / 100);
  const extraRevenuePerMonth = extraJobsPerMonth * jobValue;
  const monthlyImpact = moneySavedPerMonth + extraRevenuePerMonth;

  return {
    hoursSavedPerMonth,
    moneySavedPerMonth,
    extraJobsPerMonth,
    extraRevenuePerMonth,
    monthlyImpact,
    yearlyImpact: monthlyImpact * 12,
  };
}

/* ---------------------------------------------------------------------------
   עלות המנוי במחשבון — לפי גודל הצוות ומשקלי התפקידים בקטלוג (כיום כל משתמש
   נספר כאחד, בכל תפקיד). כל המספרים מהקטלוג.
   --------------------------------------------------------------------------- */

export interface TeamSize {
  /** בעלים, מנהלים ואנשי משרד. */
  officeUsers: number;
  /** טכנאי שטח. */
  fieldUsers: number;
}

export const DEFAULT_TEAM: TeamSize = { officeUsers: 2, fieldUsers: 3 };

/** משתמשים לחיוב: אנשי משרד וטכנאים לפי המשקל בקטלוג, מעוגל למעלה. */
export function seatsNeeded(team: TeamSize, catalog: PlanCatalog): number {
  const office = Math.floor(nonNeg(team.officeUsers));
  const field = Math.floor(nonNeg(team.fieldUsers));
  const w = catalog.seatWeights;
  // עיגול זעיר לפני ceil — כדי ש-0.5 × 3 = 1.5000000001 לא יהפוך ל-2 בטעות.
  return Math.ceil(Math.round((office * w.MANAGER + field * w.FIELD) * 1000) / 1000);
}

export interface PlanPick {
  plan: PlanKey;
  extraSeats: number;
  /** מחיר חודשי, באגורות, לפני מע"מ. */
  monthlyMinor: number;
}

/**
 * התוכנית הזולה ביותר שמכסה את מספר המשתמשים (כולל משתמשים נוספים בתוכניות
 * שמרשות אותם). null — מעבר למקסימום; "צרו קשר".
 */
export function pickPlan(
  catalog: PlanCatalog,
  seats: number,
  interval: BillingInterval = 'MONTHLY',
): PlanPick | null {
  const need = Math.max(1, Math.ceil(nonNeg(seats)));
  if (need > catalog.maxTotalSeats) return null;
  let best: PlanPick | null = null;
  for (const p of catalog.plans) {
    let extraSeats = 0;
    if (need > p.seats) {
      if (!p.extraSeatsAllowed || need - p.seats > p.maxExtraSeats) continue;
      extraSeats = need - p.seats;
    }
    const monthlyMinor = monthlyPriceMinor(catalog, p.key, interval, extraSeats);
    if (!best || monthlyMinor < best.monthlyMinor) best = { plan: p.key, extraSeats, monthlyMinor };
  }
  return best;
}

/** ההשפעה אחרי עלות המנוי (₪, לפני מע"מ). */
export function netOfSubscription(result: RoiResult, subscriptionMonthly: number) {
  const cost = nonNeg(subscriptionMonthly);
  return { netMonthly: result.monthlyImpact - cost, netYearly: (result.monthlyImpact - cost) * 12 };
}
